import React, { useState } from 'react';
import classnames from 'classnames';
import {
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Spinner,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import Requirement from './Requirement';
import SupportDocs from './SupportDocs';
import SubmitConfirmModal from '../SubmitConfirmModal';
import useInviteDmut from '../../hooks/useInviteDmut';

const InviteDmut = ({
  procId,
  procurement,
  isLoadingProcurement,
  onRefreshProcurement,
  procurementDocuments = [],
  isLoadingDocuments,
  parcels = [],
  isLoadingParcels = false,
}) => {
  const [activeTab, setActiveTab] = useState('1');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const {
    documents,
    isSubmitting,
    isGeneratingDocument,
    searchText,
    setSearchText,
    sendInvitation,
    generateDocument,
  } = useInviteDmut({
    procId,
    procurement,
    documents: procurementDocuments,
    onRefreshProcurement,
  });

  const isInviteSubmitted = procurement?.isInviteDMUTCompleted === true;
  const isDownloading = isGeneratingDocument.word || isGeneratingDocument.pdf;

  const toggleTab = tab => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const handleSendInvitation = async () => {
    await sendInvitation();
  };

  if (isLoadingProcurement && !procurement) {
    return (
      <div className='text-center py-5'>
        <Spinner color='primary' />
        <p className='text-muted mt-2 mb-0'>Loading invite DMUT data...</p>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className='d-flex align-items-center justify-content-between mb-4'>
        <div>
          <h5 className='mb-1 text-dark'>Invite DMUT</h5>
          <p className='text-muted mb-0 fs-13'>
            Review requirements and supporting documents before sending
            invitation to DMUT.
          </p>
        </div>
        <UncontrolledDropdown>
          <DropdownToggle
            color='light'
            className='border text-dark d-flex align-items-center gap-2'
            disabled={!procId || isDownloading}
          >
            {isDownloading ? (
              <>
                <Spinner size='sm' />
                Downloading...
              </>
            ) : (
              <>
                Download
                <i className='ri-download-2-line fs-16'></i>
              </>
            )}
          </DropdownToggle>
          <DropdownMenu end>
            <DropdownItem
              onClick={() => generateDocument('word')}
              disabled={isDownloading}
              className='d-flex align-items-center gap-2'
            >
              <i className='ri-file-word-2-line fs-16'></i>
              Word
            </DropdownItem>
            <DropdownItem
              onClick={() => generateDocument('pdf')}
              disabled={isDownloading}
              className='d-flex align-items-center gap-2'
            >
              <i className='ri-file-pdf-2-line fs-16'></i>
              PDF
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledDropdown>
      </div>

      <div className='checkout-tab'>
        <Nav tabs className='nav-tabs-custom nav-primary mt-1 mb-3'>
          <NavItem>
            <NavLink
              className={classnames('cursor-pointer', {
                active: activeTab === '1',
              })}
              onClick={() => toggleTab('1')}
            >
              <i className='ri-file-list-3-line me-1 align-bottom'></i>
              <span className='fw-semibold'>Requirements</span>
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames('cursor-pointer', {
                active: activeTab === '2',
              })}
              onClick={() => toggleTab('2')}
            >
              <i className='ri-file-text-line me-1 align-bottom'></i>
              <span className='fw-semibold'>Supporting Documents</span>
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          {activeTab === '1' && (
            <TabPane tabId='1'>
              <Requirement
                procurement={procurement}
                parcels={parcels}
                isLoadingParcels={isLoadingParcels}
              />
            </TabPane>
          )}
          {activeTab === '2' && (
            <TabPane tabId='2'>
              <SupportDocs
                documents={documents}
                isLoading={isLoadingDocuments}
                searchText={searchText}
                onSearchChange={setSearchText}
                onClearSearch={() => setSearchText('')}
                isReadOnly={isInviteSubmitted}
                showDmutBadge
              />
            </TabPane>
          )}
        </TabContent>
      </div>

      {!isInviteSubmitted && (
        <div className='req-actionbar'>
          <Button
            color='primary'
            type='button'
            className='d-flex align-items-center gap-2'
            disabled={isSubmitting}
            onClick={() => setShowConfirmModal(true)}
          >
            {isSubmitting ? (
              <Spinner size='sm' />
            ) : (
              <i className='ri-send-plane-fill'></i>
            )}
            <span>Send Invitation to DMUT</span>
          </Button>
        </div>
      )}

      <SubmitConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={async () => {
          setShowConfirmModal(false);
          await handleSendInvitation();
        }}
        title='Confirm Invitation to DMUT'
        message='Are you sure you want to send this invitation to DMUT? This action cannot be undone.'
        confirmLabel='Send Invitation'
        isSubmitting={isSubmitting}
      />
    </React.Fragment>
  );
};

export default InviteDmut;
